import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { FormField, useFieldIds } from "../FormField/FormField";

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  /** Acción a la derecha del label — "¿Olvidaste tu contraseña?". */
  labelAction?: ReactNode;
  hint?: ReactNode;
  /** Su presencia pone el campo en rojo y enlaza `aria-invalid`/`aria-errormessage`. */
  error?: string;
  /** Icono decorativo a la izquierda, dentro del campo. */
  icon?: ReactNode;
  /** Contenido dentro del campo a la derecha (lo usa PasswordInput). */
  trailing?: ReactNode;
  /** Clases del contenedor del campo (no del `<input>`). */
  fieldClassName?: string;
}

/**
 * Campo de texto del sistema. Se encarga solo de una cosa que las páginas
 * hacían mal a mano: enlazar label, pista, error y estados de ARIA.
 *
 * Si no recibe `id`, genera uno (`useId`) — dos campos iguales en la misma
 * pantalla no colisionan nunca.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    labelAction,
    hint,
    error,
    required,
    icon,
    trailing,
    fieldClassName,
    className,
    id,
    disabled,
    type = "text",
    "aria-describedby": extraDescribedBy,
    ...rest
  },
  ref,
) {
  const { controlId, hintId, errorId, describedBy, invalid } = useFieldIds(id, {
    hasHint: Boolean(hint),
    hasError: Boolean(error),
  });

  /*
   * `aria-describedby` admite VARIOS ids separados por espacio, y quien
   * envuelve este campo puede tener algo más que describirlo — el medidor
   * de fuerza que `PasswordField` pinta debajo, por ejemplo.
   *
   * Se compone en vez de dejar que `{...rest}` lo pise: el spread va
   * después del atributo, así que un `aria-describedby` de fuera
   * SUSTITUÍA al interno y el campo perdía el enlace con su error y su
   * pista sin que nada se viera roto. El interno va primero a propósito:
   * lo urgente (qué salió mal) se oye antes que el detalle.
   */
  const describedByAll =
    [describedBy, extraDescribedBy].filter(Boolean).join(" ") || undefined;

  return (
    <FormField
      label={label}
      labelAction={labelAction}
      hint={hint}
      error={error}
      required={required}
      controlId={controlId}
      hintId={hintId}
      errorId={errorId}
      className={fieldClassName}
    >
      <div className="relative">
        {icon && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-3.5 inline-flex items-center text-ds-ink-3 [&>svg]:h-4 [&>svg]:w-4"
          >
            {icon}
          </span>
        )}

        <input
          ref={ref}
          id={controlId}
          type={type}
          required={required}
          disabled={disabled}
          aria-invalid={invalid}
          aria-describedby={describedByAll}
          className={cn(
            "block w-full rounded-2xl border bg-ds-surface px-4 py-3 text-sm text-ds-ink",
            "outline-none transition placeholder:text-ds-ink-4",
            "focus:border-ds-lilac focus:ring-2 focus:ring-ds-lavender-bg-2/50",
            "disabled:cursor-not-allowed disabled:bg-ds-cream-2 disabled:opacity-60",
            error
              ? "border-ds-terracotta focus:border-ds-terracotta focus:ring-ds-terracotta/20"
              : "border-ds-border",
            icon && "pl-10",
            trailing && "pr-12",
            className,
          )}
          {...rest}
        />

        {trailing && (
          <span className="absolute inset-y-0 right-2 inline-flex items-center">
            {trailing}
          </span>
        )}
      </div>
    </FormField>
  );
});
